import React, { useCallback } from 'react'

import { Avatar, Box, Tooltip } from '@mantine/core'

import DnTable from 'src/components/Core/DnTable'
import SortableHeader from 'src/components/DataDisplay/SortableHeader/SortableHeader'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import { EmployeeStatus } from 'src/graphql/types/employees'
import IconEmptyInbox from 'src/icons/IconEmptyInbox'
import {
  formatDateCompact,
  formatDateSimple,
  formatDateTimeForTooltip,
  formatSidebarAddress,
  buildFullName,
  getInitials,
} from 'src/lib/formatters'
import { getLeadSourceParts } from 'src/lib/leadSource.utils'
import {
  formatInternationalPhoneNumber,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'

import ApplicantContentCell from './components/ApplicantContentCell'
import PendingActionsCell from './components/PendingActionsCell'
import StageCell from './components/StageCell'

interface SelectedApplicant {
  applicantId: string
  name: string
  email: string
}

// Maps a table column key to its backend sort field
// (ApplicantFilterPresetSortByField). Columns absent from this map are not
// sortable via header click (e.g. select, location, to-do, custom fields).
const COLUMN_SORT_FIELDS: Record<string, string> = {
  applicant: 'APPLICANT_NAME',
  position: 'POSITION',
  employees: 'RECRUITER',
  leadSource: 'LEAD_SOURCE',
  calls: 'CALL_COUNT',
  missedCalls: 'MISSED_CALL_COUNT',
  applied: 'APPLICATION_DATE',
  lastContact: 'LAST_CONTACT_DATE',
  hired: 'HIRED_DATE',
  terminated: 'TERMINATED_DATE',
  disqualified: 'DISQUALIFIED_DATE',
  notInterested: 'NOT_INTERESTED_DATE',
  availableForWork: 'AVAILABLE_FOR_WORK_DATE',
  stage: 'APPLICANT_STAGE',
  experience: 'EXPERIENCE',
}

interface ApplicantsTableProps {
  applicantPage?: { items: unknown[] }
  selectedApplicants: SelectedApplicant[]
  setSelectedApplicants: React.Dispatch<
    React.SetStateAction<SelectedApplicant[]>
  >
  openDrawer: (applicantId: string) => void
  loading: boolean
  onPhoneCall: (params: {
    applicantId: string
    applicantFullName: string
    phone: string
  }) => void
  columnVisibility?: Record<string, boolean>
  columnOrder?: string[]
  customFieldColumns?: Array<{
    key: string
    label: string
    hidden: boolean
    width?: number
    render: (row: any) => any
  }>
  stepColumns?: Array<{
    key: string
    label: string
    hidden: boolean
    render: (row: any) => any
  }>
  // Single-field header sort. When onSortChange is provided, sortable column
  // headers become clickable.
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void
}

const ApplicantsTable = ({
  applicantPage = { items: [] },
  selectedApplicants,
  setSelectedApplicants,
  openDrawer,
  loading,
  onPhoneCall,
  columnVisibility = {},
  columnOrder = [],
  customFieldColumns = [],
  stepColumns = [],
  sortField,
  sortDirection = 'asc',
  onSortChange,
}: ApplicantsTableProps) => {
  const onRowClick = useCallback(
    (rowIndex, applicant) => {
      openDrawer(applicant.applicantId)
    },
    [openDrawer]
  )

  const callCountLabel = useCallback((callCount) => {
    if (callCount === null || callCount === 0) {
      return '0 calls'
    }
    if (callCount === 1) {
      return '1 call'
    } else if (callCount > 1) {
      return `${callCount} calls`
    }
  }, [])

  const missedCallCountLabel = useCallback((missedCallCount) => {
    if (missedCallCount === null || missedCallCount === 0) {
      return '0 missed'
    }
    if (missedCallCount === 1) {
      return '1 missed'
    } else if (missedCallCount > 1) {
      return `${missedCallCount} missed`
    }
  }, [])

  const handleSelectAll = useCallback(() => {
    if (
      applicantPage.items.every((item) =>
        selectedApplicants.find(
          (selected) => selected.applicantId === item.applicantId
        )
      )
    ) {
      setSelectedApplicants((prev) =>
        prev.filter(
          (item) =>
            !applicantPage.items.find(
              (applicant) => applicant.applicantId === item.applicantId
            )
        )
      )
    } else {
      const duplicateFreeArray = applicantPage.items.filter((item) => {
        return !selectedApplicants.find(
          (selected) => selected.applicantId === item.applicantId
        )
      })
      const temp = duplicateFreeArray.map((item) => {
        return {
          applicantId: item.applicantId,
          name: buildFullName(item),
          email: item.email,
        }
      })
      setSelectedApplicants((prev) => [...prev, ...temp])
    }
  }, [applicantPage.items, selectedApplicants, setSelectedApplicants])

  const handleSelectApplicant = useCallback(
    (applicant) => {
      setSelectedApplicants((prev) =>
        prev.find((item) => item.applicantId === applicant.applicantId)
          ? prev.filter((item) => item.applicantId !== applicant.applicantId)
          : [
              ...prev,
              {
                applicantId: applicant.applicantId,
                name: buildFullName(applicant),
                email: applicant.email,
              },
            ]
      )
    },
    [setSelectedApplicants]
  )

  // Define all possible columns
  const allColumns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={
            !loading && applicantPage.items.length > 0
              ? applicantPage.items.every((item) =>
                  selectedApplicants.find(
                    (selected) => selected.applicantId === item.applicantId
                  )
                )
              : false
          }
          onChange={handleSelectAll}
        />
      ),
      hidden: !(columnVisibility.select ?? true),
      render: (applicant) => (
        <div>
          <input
            type="checkbox"
            checked={
              !!selectedApplicants.find(
                (item) => item.applicantId === applicant.applicantId
              )
            }
            onClick={(e) => {
              e.stopPropagation()
            }}
            onChange={() => handleSelectApplicant(applicant)}
          />
        </div>
      ),
    },
    {
      key: 'applicant',
      label: 'Applicant',
      hidden: !(columnVisibility.applicant ?? true),
      render: (applicant) => (
        <div className="flex w-full items-center">
          <ApplicantContentCell applicant={applicant} />
        </div>
      ),
    },
    {
      key: 'position',
      label: 'Position',
      hidden: !(columnVisibility.position ?? true),
      render: (applicant) => (
        <div className="flex max-w-80 flex-col gap-1">
          <Tooltip label={applicant.jobListing?.title ?? '-'} withArrow>
            <div className="w-full overflow-hidden truncate">
              {applicant.jobListing?.title ?? '-'}
            </div>
          </Tooltip>

          <div className="flex w-full flex-row items-center gap-1 overflow-auto">
            {applicant?.tags?.map((tag) => (
              <Tag
                key={tag.tagId}
                size="xs"
                variant="secondary"
                showCloseButton={false}
              >
                {tag.label}
              </Tag>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: 'employees',
      label: 'Employee(s)',
      hidden: !(columnVisibility.employees ?? false),
      render: (applicant) => {
        const recruiter = applicant.recruiter
        const members = applicant.memberEmployees || []

        if (!recruiter) {
          return (
            <div className="text-xs font-medium text-doubleNickel-gray-900">
              -
            </div>
          )
        }

        const recruiterName = buildFullName(recruiter)
        const recruiterInitials = getInitials(recruiterName)

        return (
          <div className="flex items-center">
            <Avatar.Group spacing="0">
              <Tooltip label={recruiterName} withArrow>
                <Avatar
                  classNames={{
                    placeholder:
                      recruiter.status !== EmployeeStatus.ACTIVE
                        ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
                        : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                  }}
                  size="sm"
                  radius="sm"
                >
                  {recruiterInitials}
                </Avatar>
              </Tooltip>
            </Avatar.Group>
            {members.length > 0 && (
              <>
                <span className="mx-1.5 text-xs text-doubleNickel-gray-300">
                  |
                </span>
                <Avatar.Group spacing="0">
                  {/* With exactly two members both fit, so show initials
                      instead of a "+1" overflow badge */}
                  {(members.length === 2 ? members : members.slice(0, 1)).map(
                    (member) => {
                      const memberName = buildFullName(member)
                      const memberInitials = getInitials(memberName)
                      return (
                        <Tooltip
                          key={member.employeeId}
                          label={memberName}
                          withArrow
                        >
                          <Avatar
                            classNames={{
                              placeholder:
                                member.status !== EmployeeStatus.ACTIVE
                                  ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
                                  : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                            }}
                            size="sm"
                            radius="sm"
                          >
                            {memberInitials}
                          </Avatar>
                        </Tooltip>
                      )
                    }
                  )}
                  {members.length > 2 && (
                    <Tooltip
                      label={members
                        .slice(1)
                        .map((m) => buildFullName(m))
                        .join(', ')}
                      withArrow
                    >
                      <Avatar
                        classNames={{
                          // if all members are inactive, use warning color, otherwise use brand color
                          placeholder: members
                            .slice(1)
                            .every((m) => m.status !== EmployeeStatus.ACTIVE)
                            ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
                            : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                        }}
                        size="sm"
                        radius="sm"
                      >
                        +{members.length - 1}
                      </Avatar>
                    </Tooltip>
                  )}
                </Avatar.Group>
              </>
            )}
          </div>
        )
      },
    },
    {
      key: 'leadSource',
      label: 'Lead Source',
      hidden: !(columnVisibility.leadSource ?? false),
      render: (applicant) => {
        // Show the type on top and the customizable link name on a muted
        // second line so recruiters can tell links apart within a type
        // (e.g. "Recruiter" / "Allison") without the cell overflowing.
        const { typeLabel, linkName } = getLeadSourceParts(
          applicant.application?.trackingLink
        )

        if (!typeLabel) {
          return (
            <div className="text-xs font-medium text-doubleNickel-gray-900">
              -
            </div>
          )
        }

        return (
          <div className="flex flex-col">
            <div className="truncate text-xs font-medium text-doubleNickel-gray-900">
              {typeLabel}
            </div>
            {linkName && (
              <Tooltip label={linkName} withArrow>
                <div className="truncate text-xs text-doubleNickel-gray-600">
                  {linkName}
                </div>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      key: 'contact',
      label: 'Contact',
      hidden: !(columnVisibility.contact ?? true),
      render: (applicant) => (
        <Box
          onClick={(event) => {
            event.stopPropagation()
            onPhoneCall({
              applicantId: applicant.applicantId,
              applicantFullName: buildFullName(applicant),
              phone: unformatPhoneNumber(applicant.phone),
            })
          }}
          className={`cursor-pointer text-nowrap text-xs font-medium text-doubleNickel-brand-500 hover:underline`}
        >
          {formatInternationalPhoneNumber(applicant.phone)}
        </Box>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      hidden: !(columnVisibility.email ?? false),
      render: (applicant) => (
        <div className="truncate text-xs font-medium text-doubleNickel-gray-900">
          {applicant.email || '-'}
        </div>
      ),
    },
    {
      key: 'calls',
      label: 'Call Count',
      hidden: !(columnVisibility.calls ?? true),
      render: (applicant) => (
        <div className="text-xs font-medium text-doubleNickel-gray-900">
          {callCountLabel(applicant.callCount)}
        </div>
      ),
    },
    {
      key: 'missedCalls',
      label: 'Missed Calls',
      hidden: !(columnVisibility.missedCalls ?? false),
      render: (applicant) => (
        <div className="text-xs font-medium text-doubleNickel-gray-900">
          {missedCallCountLabel(applicant.missedOutboundCallCount)}
        </div>
      ),
    },
    {
      key: 'applied',
      label: 'Applied Date',
      hidden: !(columnVisibility.applied ?? true),
      render: (applicant) => (
        <Tooltip
          label={formatDateTimeForTooltip(applicant.applicationDate)}
          withArrow
          disabled={!applicant.applicationDate}
        >
          <span>{formatDateCompact(applicant.applicationDate)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'lastContact',
      label: 'Last Contact Date',
      hidden: !(columnVisibility.lastContact ?? false),
      render: (applicant) => (
        <Tooltip
          label={formatDateTimeForTooltip(applicant.lastContactDate)}
          withArrow
          disabled={!applicant.lastContactDate}
        >
          <span>{formatDateCompact(applicant.lastContactDate)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'hired',
      label: 'Hired Date',
      hidden: !(columnVisibility.hired ?? false),
      render: (applicant) => formatDateSimple(applicant.hiredDate),
    },
    {
      key: 'terminated',
      label: 'Terminated Date',
      hidden: !(columnVisibility.terminated ?? false),
      render: (applicant) => formatDateSimple(applicant.terminatedDate),
    },
    {
      key: 'disqualified',
      label: 'Disqualified Date',
      hidden: !(columnVisibility.disqualified ?? false),
      render: (applicant) => formatDateSimple(applicant.disqualifiedDate),
    },
    {
      key: 'notInterested',
      label: 'Not Interested Date',
      hidden: !(columnVisibility.notInterested ?? false),
      render: (applicant) => formatDateSimple(applicant.notInterestedDate),
    },
    {
      key: 'availableForWork',
      label: 'Available for Work Date',
      hidden: !(columnVisibility.availableForWork ?? false),
      render: (applicant) =>
        formatDateSimple(applicant.application?.availableForWork),
    },
    {
      key: 'stage',
      label: 'Stage',
      hidden: !(columnVisibility.stage ?? true),
      render: (applicant) => <StageCell applicant={applicant} />,
    },
    {
      key: 'experience',
      label: 'Experience',
      hidden: !(columnVisibility.experience ?? true),
      render: (applicant) =>
        applicant.yearsOfExperience !== null &&
        applicant.yearsOfExperience !== undefined ? (
          <div className="flex flex-col">
            {/* Years */}
            {Math.floor(applicant.yearsOfExperience)} year
            {Math.floor(applicant.yearsOfExperience) !== 1 && 's'}
            {/* Months */}
            <div className="truncate text-xs text-doubleNickel-gray-600">
              {Math.round((applicant.yearsOfExperience % 1) * 12)} month
              {Math.round((applicant.yearsOfExperience % 1) * 12) !== 1 && 's'}
            </div>
          </div>
        ) : (
          '-'
        ),
    },
    {
      key: 'location',
      label: 'Location',
      hidden: !(columnVisibility.location ?? true),
      render: (applicant) => (
        <div className="flex flex-col">
          {formatSidebarAddress(applicant.address).split(',')[0]}
          <div className="truncate text-xs text-doubleNickel-gray-600">
            {formatSidebarAddress(applicant.address).split(',')[1]}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'To Do',
      hidden: !(columnVisibility.actions ?? true),
      render: (applicant) => (
        <div className="overflow-hidden">
          <PendingActionsCell
            steps={applicant.steps}
            tasks={applicant.tasks}
            bookings={applicant.bookings}
            expirationTrackers={applicant.expirationTrackers}
            applicantId={applicant.applicantId}
          />
        </div>
      ),
    },
  ]

  // Merge custom field and step columns with standard columns
  const mergedColumns = [
    ...allColumns,
    ...customFieldColumns.map((col) => ({
      ...col,
      hidden: !(columnVisibility[col.key] ?? false), // Apply visibility from columnVisibility
    })),
    ...stepColumns.map((col) => ({
      ...col,
      hidden: !(columnVisibility[col.key] ?? false), // Apply visibility from columnVisibility
    })),
  ]

  // Order columns based on columnOrder prop
  const orderedColumns =
    columnOrder.length > 0
      ? columnOrder
          .map((key) => mergedColumns.find((col) => col.key === key))
          .filter(Boolean)
      : mergedColumns

  // Make sortable column headers clickable when a sort handler is provided.
  const columns = onSortChange
    ? orderedColumns.map((col) => {
        const fieldForColumn = COLUMN_SORT_FIELDS[col.key]
        if (fieldForColumn && typeof col.label === 'string') {
          return {
            ...col,
            label: (
              <SortableHeader
                label={col.label}
                field={fieldForColumn}
                sortField={sortField ?? ''}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            ),
          }
        }
        return col
      })
    : orderedColumns

  return (
    <div className="w-full">
      <DnTable
        columns={columns}
        data={applicantPage.items || []}
        loading={loading}
        onRowClick={onRowClick}
        rowClassName="h-10 text-xs font-medium text-doubleNickel-gray-900 hover:bg-doubleNickel-gray-50"
        headerClassName="h-11 border-none text-xs text-doubleNickel-gray-600"
        emptyStateMessage={
          <div className="flex flex-1 flex-col items-center justify-center">
            <IconEmptyInbox className="h-40 fill-none" />
            <div className="text-md font-medium text-doubleNickel-gray-800">
              No Applicants Found
            </div>
            <div className="text-xs text-doubleNickel-gray-600">
              Add a new applicant to get started
            </div>
          </div>
        }
        enablePagination={false}
        style={{ width: '100%' }}
      />
    </div>
  )
}

export default ApplicantsTable
